import { useState, useEffect, useRef } from 'react';

declare global {
  interface Window {
    turnstile: any;
  }
}

interface UseTurnstileProps {
  action: string;
  theme?: 'light' | 'dark';
}

export const useTurnstile = ({ action, theme = 'dark' }: UseTurnstileProps) => {
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const turnstileWidgetRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const turnstileSiteKey = import.meta.env.DEV
    ? "1x00000000000000000000AA" // Test key for 'passes'
    : import.meta.env['VITE_TURNSTILE_SITE_KEY'];

  useEffect(() => {
    const container = turnstileWidgetRef.current;
    if (!container || !turnstileSiteKey) {
      if (!turnstileSiteKey && !import.meta.env.DEV) {
        console.error("[useTurnstile] Turnstile site key is not configured for production.");
      }
      return undefined;
    }

    const intervalId = setInterval(() => {
      if (window.turnstile) {
        clearInterval(intervalId);
        widgetIdRef.current = window.turnstile.render(container, {
          sitekey: turnstileSiteKey,
          action: action,
          callback: (token: string) => {
            setTurnstileToken(token);
            setTurnstileError(null);
          },
          'error-callback': () => {
            setTurnstileError("CAPTCHA verification failed. Please try again.");
          },
          'expired-callback': () => {
            setTurnstileError("CAPTCHA token expired. Please complete the verification again.");
            setTurnstileToken(null);
          },
          theme: theme,
        });
      }
    }, 100);

    return () => {
      clearInterval(intervalId);
      if (widgetIdRef.current && window.turnstile) {
        const widgetElement = document.querySelector(`[data-widget-id='${widgetIdRef.current}']`);
        if (widgetElement) {
          window.turnstile.remove(widgetIdRef.current);
        }
      }
    };
  }, [action, theme, turnstileSiteKey]);

  const resetTurnstile = () => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  };

  return { turnstileToken, turnstileError, turnstileWidgetRef, setTurnstileError, resetTurnstile };
};